import React, { useEffect, useState, useCallback, useRef } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { IconButton } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { useMutation } from '@tanstack/react-query'
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined'

import effects from 'app/effects/effects'
import { currentTotalToPercentage } from 'libs/math'
import { resize_250_q40 } from 'commons/resizeImage'
import MessageAlert from 'app/components/MessageAlert'

const useStyles = makeStyles((theme) => ({
  photoContainer: {
    position: 'relative',
    display: 'inline-block',
    height: 150, // match image height
    width: 150, // match image width (so container fits exactly)
    margin: theme.spacing(1.25), // ~10px
    marginBottom: 0,
  },
  photoContainerLoading: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    background: '#ffffff',
    opacity: 0.8,
    zIndex: 1,
    borderRadius: 8, // ensure overlay respects the radius
  },
  photoImg: {
    width: 150,
    height: 150,
    objectFit: 'cover',
    borderRadius: 8, // 8px radius on the image
    display: 'block', // remove any inline‐block whitespace
  },
  deleteContainer: {
    position: 'absolute',
    right: theme.spacing(1.125), // ~9px
    bottom: theme.spacing(0.875), // ~7px
    background: theme.colors['primary-350'],
    width: 23,
    height: 23,
    fontSize: 2,
    '&:hover': {
      backgroundColor: theme.colors['primary-350'],
    },
    zIndex: 2,
    borderRadius: 6, // keep the delete button round
  },
  deleteIc: {
    fontSize: '1.8rem',
    color: theme.colors['red-500'],
  },
  progressContainer: {
    height: 10,
    background: 'rgba(6, 78, 117, 0.19)',
    position: 'absolute',
    bottom: theme.spacing(1.5), // ~12px
    left: theme.spacing(1.25), // ~10px
    right: theme.spacing(1.25), // ~10px
    borderRadius: 6,
    zIndex: 2,
  },
  progressBar: {
    background: theme.colors['green-500'],
    height: '100%',
    borderRadius: 6,
  },
}))

export const Photo = ({
  alt,
  img,
  index,
  onReject,
  onDelete,
  onLoading,
  onUploadSuccess,
}) => {
  const classes = useStyles()
  const { formatMessage } = useIntl()

  const {
    id: originalId,
    uploadId,
    url,
    isLoading,
    isDeleting,
    isCancel,
    type,
  } = img

  const dispatch = useDispatch()

  const [percentage, setPercentage] = useState(0)
  const cancellableRef = useRef(new AbortController())

  const handleProgress = useCallback(
    ({ loaded, total }) => {
      const pct = currentTotalToPercentage(loaded, total)
      setPercentage(pct)
    },
    [setPercentage],
  )

  const uploadPhoto = useMutation({
    mutationFn: async (url, uploadId) => {
      const response = await effects.apis.profileInfo.uploadPhoto(
        {
          file: url,
        },
        handleProgress,
        uploadId,
        cancellableRef.current.signal,
      )
      return response
    },
    onSuccess: ({ photoId }) => {
      onUploadSuccess(photoId, uploadId)
    },
    onError: (error) => {
      onReject(uploadId)
      dispatch(MessageAlert.showNetworkError(formatMessage)(error))
    },
  })

  useEffect(() => {
    if (isLoading) {
      onLoading(uploadId, true)
      uploadPhoto.mutate(url, uploadId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isCancel) cancellableRef?.current?.abort()
  }, [isCancel])

  const handleDelete = useCallback(
    (e) => {
      e.stopPropagation()
      onDelete(originalId)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [originalId, uploadId],
  )

  return (
    <div className={clsx(classes.photoContainer)}>
      {/* type === 1 is from API and has url image kit, so need resize */}
      <img
        className={classes.photoImg}
        src={type === 1 ? resize_250_q40(url) : url}
        alt={alt}
      />

      {/* Show delete button only when not uploading */}
      {!uploadPhoto.isPending && !isDeleting && (
        <IconButton
          className={classes.deleteContainer}
          onClick={handleDelete}
          aria-label="delete photo"
        >
          <DeleteOutlineOutlinedIcon className={classes.deleteIc} />
        </IconButton>
      )}

      {/* Overlay + progress bar */}
      {isDeleting && <div className={classes.photoContainerLoading} />}
      {uploadPhoto.isPending && (
        <>
          <div className={classes.photoContainerLoading} />
          <div className={classes.progressContainer}>
            <div
              className={classes.progressBar}
              style={{ width: `${percentage}%` }}
            />
          </div>
        </>
      )}
    </div>
  )
}

Photo.propTypes = {
  index: PropTypes.number,
  onReject: PropTypes.func,
  onLoading: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
  onUploadSuccess: PropTypes.func.isRequired,
  img: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    url: PropTypes.string.isRequired,
    isLoading: PropTypes.bool,
    isDeleting: PropTypes.bool,
    uploadId: PropTypes.number,
    isCancel: PropTypes.bool,
    type: PropTypes.number,
  }).isRequired,
}
